import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Search, BookOpen, Users, X, SlidersHorizontal, RotateCcw, Sparkles, Shuffle, RefreshCw } from 'lucide-react';
import { getCatalogue, getExplorer, getSurprise } from '../services/cms';
import { useTexts } from '../context/TextsContext';
import EditorialCard from '../components/EditorialCard';
import { StatBadge } from '../components/StatBadge';

const facetFrom = (episodes, key) => {
  const map = new Map();
  episodes.forEach((e) => (e[key] || []).forEach((t) => t.slug && map.set(t.slug, t.name)));
  return [...map.entries()].map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
};

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-800 uppercase text-muted-foreground mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-muted font-700 text-sm outline-none cursor-pointer">
      <option value="">Tous</option>
      {options.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
    </select>
  </div>
);

const Catalogue = () => {
  const { t } = useTexts();
  const [episodes, setEpisodes] = React.useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [explorer, setExplorer] = useState(null);
  const [shuffle, setShuffle] = useState(null);
  const [surprising, setSurprising] = useState(false);
  const discoverRef = useRef(null);

  const q = params.get('q') || '';
  const fAuteur = params.get('auteur') || '';
  const fCollection = params.get('collection') || '';
  const fChapitre = params.get('chapitre') || '';
  const fLieu = params.get('lieu') || '';
  const hasFilters = q || fAuteur || fCollection || fChapitre || fLieu;

  const set = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };
  const reset = () => setParams({}, { replace: true });

  useEffect(() => { getCatalogue().then(setEpisodes).catch(() => setEpisodes([])); }, []);
  useEffect(() => { getExplorer(shuffle).then(setExplorer).catch(() => setExplorer({ featured: [], selection: [] })); }, [shuffle]);

  const renew = () => { setShuffle(Date.now().toString()); setTimeout(() => discoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350); };
  const surprise = async () => {
    setSurprising(true);
    try { const r = await getSurprise(); if (r.slug) navigate(`/fiche/${encodeURIComponent(r.slug)}${r.collection_slug ? `?collection=${encodeURIComponent(r.collection_slug)}` : ''}`); }
    finally { setSurprising(false); }
  };

  const facets = useMemo(() => episodes ? {
    auteurs: facetFrom(episodes, 'auteurs'),
    collections: facetFrom(episodes, 'collections'),
    chapters: facetFrom(episodes, 'chapters'),
    locations: facetFrom(episodes, 'locations'),
  } : { auteurs: [], collections: [], chapters: [], locations: [] }, [episodes]);

  const results = useMemo(() => {
    if (!episodes) return [];
    let list = episodes;
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter((e) => e.titre.toLowerCase().includes(s)); }
    const has = (arr, slug) => (arr || []).some((t) => t.slug === slug);
    if (fAuteur) list = list.filter((e) => has(e.auteurs, fAuteur));
    if (fCollection) list = list.filter((e) => has(e.collections, fCollection));
    if (fChapitre) list = list.filter((e) => has(e.chapters, fChapitre));
    if (fLieu) list = list.filter((e) => has(e.locations, fLieu));
    return list;
  }, [episodes, q, fAuteur, fCollection, fChapitre, fLieu]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 min-h-[60vh]">
      <h1 className="font-display font-700 text-4xl text-foreground mb-3 flex items-center gap-3"><BookOpen className="w-8 h-8 text-primary" /> {t('catalogue_title', 'Le catalogue Citytoon')}</h1>
      <p className="text-foreground/80 font-600 mb-2 max-w-2xl text-base">{t('catalogue_text', '')}</p>
      {t('catalogue_secondary', '') && <p className="text-secondary font-800 mb-8">{t('catalogue_secondary', '')}</p>}

      {explorer && explorer.featured && explorer.featured.length > 0 && (
        <section className="mb-10" data-testid="explorer-featured">
          <h2 className="font-display font-700 text-2xl text-foreground mb-4 flex items-center gap-2.5"><Sparkles className="w-6 h-6 text-primary" /> À la une</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {explorer.featured.map((c, i) => <EditorialCard key={c.id} card={c} testid={`featured-card-${i}`} />)}
          </div>
        </section>
      )}

      {explorer && explorer.enabled !== false && (
        <section ref={discoverRef} className="mb-12" data-testid="explorer-discover">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-display font-700 text-2xl text-foreground flex items-center gap-2.5"><Shuffle className="w-6 h-6 text-primary" /> À découvrir aujourd’hui</h2>
            <div className="flex gap-2">
              <button onClick={renew} data-testid="explorer-renew-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-800 text-sm hover:bg-primary hover:text-white transition-colors"><RefreshCw className="w-4 h-4" /> Renouveler la sélection</button>
              <button onClick={surprise} disabled={surprising} data-testid="explorer-surprise-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground font-800 text-sm hover:scale-105 transition-transform disabled:opacity-60">{surprising ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Me surprendre</button>
            </div>
          </div>
          {explorer.selection === null ? null : explorer.selection.length === 0 ? (
            <p className="text-muted-foreground font-600">Aucune sélection disponible.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-5">
              {explorer.selection.map((e, i) => (
                <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}${e.collection_slug ? `?collection=${encodeURIComponent(e.collection_slug)}` : ''}`} data-testid={`discover-card-${e.slug}`}
                  className="card-hover group bg-card rounded-2xl overflow-hidden border border-border animate-float-up" style={{ animationDelay: `${Math.min(i * 40, 300)}ms` }}>
                  <div className="relative aspect-square overflow-hidden">
                    {e.featured_image?.url ? <img src={e.featured_image.url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
                  </div>
                  <div className="p-3"><h3 className="font-display font-600 text-sm leading-snug line-clamp-2 min-h-[36px] group-hover:text-primary transition-colors">{e.titre}</h3></div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {episodes === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input value={q} onChange={(e) => set('q', e.target.value)} placeholder={t('catalogue_search_placeholder', 'Rechercher un titre...')}
              className="w-full pl-12 pr-10 py-3 rounded-full bg-muted font-600 outline-none focus:ring-2 focus:ring-primary/40" />
            {q && <button onClick={() => set('q', '')} className="absolute right-4 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-muted-foreground" /></button>}
          </div>

          <div className="bg-card border border-border rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-2 font-800 text-sm"><SlidersHorizontal className="w-4 h-4 text-primary" /> {t('catalogue_filters_label', 'Filtres')}</span>
              {hasFilters && <button onClick={reset} className="flex items-center gap-1.5 text-sm font-800 text-primary hover:underline"><RotateCcw className="w-3.5 h-3.5" /> {t('catalogue_reset_label', 'Réinitialiser')}</button>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <FilterSelect label="Auteurs et autrices" value={fAuteur} onChange={(v) => set('auteur', v)} options={facets.auteurs} />
              <FilterSelect label="Collection" value={fCollection} onChange={(v) => set('collection', v)} options={facets.collections} />
              <FilterSelect label="Chapitre" value={fChapitre} onChange={(v) => set('chapitre', v)} options={facets.chapters} />
              <FilterSelect label="Lieu" value={fLieu} onChange={(v) => set('lieu', v)} options={facets.locations} />
            </div>
          </div>

          <p className="text-sm text-muted-foreground font-700 mb-5">{t('catalogue_results_label', '{count} webtoon(s)').replace('{count}', results.length)}</p>

          {results.length === 0 ? (
            <div className="text-center py-20"><p className="font-display font-600 text-xl text-muted-foreground">{t('catalogue_empty', 'Aucun résultat.')}</p></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {results.map((e, i) => (
                <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}`} data-testid={`catalogue-card-${e.slug}`} className="card-hover group bg-card rounded-2xl overflow-hidden border border-border animate-float-up" style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>
                  <div className="relative aspect-square overflow-hidden">
                    {e.featured_image?.url ? <img src={e.featured_image.url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="absolute bottom-2 left-2 right-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-800 shadow-lg opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all"><BookOpen className="w-3.5 h-3.5" /> Lire le webtoon</span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-display font-600 text-sm leading-snug line-clamp-2 min-h-[36px] group-hover:text-primary transition-colors">{e.titre}</h3>
                    {(e.auteurs || []).length > 0 && (
                      <p className="text-[11px] text-muted-foreground font-700 mt-1 flex items-center gap-1 truncate"><Users className="w-3 h-3 shrink-0" /> {e.auteurs.map((a) => a.name).join(', ')}</p>
                    )}
                    <StatBadge stats={e.stats} className="mt-1.5 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Catalogue;
